/**
 * Phase SC-22c — learner seat plans (mixed seating).
 *
 *   GET    /api/seating/learner-plans            (paged, `kind` filter, name search)
 *   POST   /api/seating/learner-plans:bulk       (one plan for many learners)
 *   GET    /api/seating/learners/{id}/plan
 *   PUT    /api/seating/learners/{id}/plan
 *   DELETE /api/seating/learners/{id}/plan       (back to the centre default)
 *   POST   /api/seating/learners/{id}/check-in   (walk-in check-in for a no-seat learner)
 *
 * Shapes mirror `app/domains/seating/schemas.py` (`LearnerSeatPlanBody`,
 * `EffectiveSeatPlanResponse`, `LearnerSeatPlanListResponse`,
 * `BulkLearnerSeatPlanResponse`). Reads need `manage_zones` or
 * `manage_bookings`; writes need `manage_zones`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";
import type { SeatAssignmentMode, MixedDefaultPlan } from "@/lib/seatAssignmentConfig/api";
import { seatAssignmentKeys } from "@/lib/seating/allocations";

export type SeatPlanKind =
  | "fixed_seat"
  | "first_come"
  | "fixed_hours"
  | "flexible_hours"
  | "zone_only"
  | "no_seat";

export type SeatPlanSource = "learner_plan" | "mixed_default";

export interface LearnerSeatPlanBody {
  kind: SeatPlanKind;
  zone_id?: string | null;
  seat_id?: string | null;
  slot_keys?: string[];
  /** `HH:MM:SS`, centre-local. Set together with `window_end`. */
  window_start?: string | null;
  window_end?: string | null;
  /** `YYYY-MM-DD`, inclusive. */
  valid_from?: string | null;
  valid_until?: string | null;
  note?: string | null;
}

export interface EffectiveSeatPlan {
  student_id: string;
  kind: SeatPlanKind;
  source: SeatPlanSource;
  zone_id: string | null;
  zone_name: string | null;
  seat_id: string | null;
  seat_number: string | null;
  slot_keys: string[];
  window_start: string | null;
  window_end: string | null;
  valid_from: string | null;
  valid_until: string | null;
  note: string | null;
  updated_at: string | null;
}

export interface LearnerSeatPlanRow extends EffectiveSeatPlan {
  learner_name: string;
}

export interface LearnerSeatPlanListResponse {
  items: LearnerSeatPlanRow[];
  total: number;
  centre_mode: SeatAssignmentMode;
  mixed_default_plan: MixedDefaultPlan;
}

export interface BulkSeatPlanFailure {
  student_id: string;
  code: string;
  message: string;
}

export interface BulkSeatPlanResponse {
  updated: number;
  failed: BulkSeatPlanFailure[];
}

export interface SeatPlanListFilters {
  kind?: SeatPlanKind;
  q?: string;
  branch_id?: string;
  limit?: number;
  offset?: number;
}

export const SEAT_PLAN_KINDS: readonly SeatPlanKind[] = [
  "fixed_seat",
  "first_come",
  "fixed_hours",
  "flexible_hours",
  "zone_only",
  "no_seat",
];

export const SEAT_PLAN_LABELS: Record<SeatPlanKind, string> = {
  fixed_seat: "Permanent seat",
  first_come: "First come",
  fixed_hours: "Fixed hours",
  flexible_hours: "Flexible hours",
  zone_only: "Room only",
  no_seat: "No seat",
};

export const SEAT_PLAN_HELP: Record<SeatPlanKind, string> = {
  fixed_seat: "The same seat every day. Nobody else can book it.",
  first_come: "Books any free seat for a shift.",
  fixed_hours: "Only the chosen shifts or daily hours. Can hold one seat for those hours.",
  flexible_hours: "Books whole hours inside opening hours, charged at the hourly price.",
  zone_only: "Sits anywhere in one room. The room's capacity is always enforced.",
  no_seat: "No booking. Checks in at the desk on arrival.",
};

export const seatPlanKeys = {
  all: ["ia", "seating", "learner-plans"] as const,
  list: (filters: SeatPlanListFilters) => [...seatPlanKeys.all, "list", filters] as const,
  one: (studentId: string) => [...seatPlanKeys.all, "one", studentId] as const,
};

export function buildPlanListQuery(filters: SeatPlanListFilters): string {
  const search = new URLSearchParams();
  if (filters.kind) search.set("kind", filters.kind);
  if (filters.q?.trim()) search.set("q", filters.q.trim());
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useLearnerSeatPlans(filters: SeatPlanListFilters) {
  const qs = buildPlanListQuery(filters);
  return useQuery<LearnerSeatPlanListResponse>({
    queryKey: seatPlanKeys.list(filters),
    queryFn: () =>
      api.get<LearnerSeatPlanListResponse>(`/api/seating/learner-plans${qs ? `?${qs}` : ""}`),
    staleTime: 10_000,
    placeholderData: (previous) => previous,
  });
}

function invalidatePlans(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: seatPlanKeys.all });
  // A permanent-seat plan creates or releases a SeatAssignment.
  void qc.invalidateQueries({ queryKey: seatAssignmentKeys.all });
}

export interface PutSeatPlanVars {
  studentId: string;
  body: LearnerSeatPlanBody;
}

export function usePutLearnerSeatPlan() {
  const qc = useQueryClient();
  return useMutation<EffectiveSeatPlan, ApiError, PutSeatPlanVars>({
    mutationKey: [...seatPlanKeys.all, "put"],
    mutationFn: ({ studentId, body }) =>
      api.put<EffectiveSeatPlan>(
        `/api/seating/learners/${encodeURIComponent(studentId)}/plan`,
        normalisePlanBody(body),
      ),
    onSuccess: () => invalidatePlans(qc),
  });
}

export function useClearLearnerSeatPlan() {
  const qc = useQueryClient();
  return useMutation<EffectiveSeatPlan, ApiError, string>({
    mutationKey: [...seatPlanKeys.all, "delete"],
    mutationFn: (studentId) =>
      api.delete<EffectiveSeatPlan>(`/api/seating/learners/${encodeURIComponent(studentId)}/plan`),
    onSuccess: () => invalidatePlans(qc),
  });
}

export interface BulkSeatPlanVars {
  studentIds: string[];
  body: LearnerSeatPlanBody;
}

export function useBulkLearnerSeatPlans() {
  const qc = useQueryClient();
  return useMutation<BulkSeatPlanResponse, ApiError, BulkSeatPlanVars>({
    mutationKey: [...seatPlanKeys.all, "bulk"],
    mutationFn: ({ studentIds, body }) =>
      api.post<BulkSeatPlanResponse>("/api/seating/learner-plans:bulk", {
        ...normalisePlanBody(body),
        student_ids: studentIds,
      }),
    onSuccess: () => invalidatePlans(qc),
  });
}

/**
 * The BE rejects fields a plan kind does not use (422), so the body only
 * carries what the chosen kind reads. Empty strings from form inputs become
 * absent fields.
 */
export function normalisePlanBody(body: LearnerSeatPlanBody): LearnerSeatPlanBody {
  const clean = (v: string | null | undefined): string | null => (v && v.trim() ? v : null);
  const out: LearnerSeatPlanBody = {
    kind: body.kind,
    valid_from: clean(body.valid_from),
    valid_until: clean(body.valid_until),
    note: clean(body.note),
  };
  switch (body.kind) {
    case "fixed_seat":
      out.seat_id = clean(body.seat_id);
      out.zone_id = clean(body.zone_id);
      break;
    case "fixed_hours": {
      out.seat_id = clean(body.seat_id);
      out.zone_id = clean(body.zone_id);
      const start = clean(body.window_start);
      const end = clean(body.window_end);
      if (start && end) {
        out.window_start = withSeconds(start);
        out.window_end = withSeconds(end);
        out.slot_keys = [];
      } else {
        out.slot_keys = body.slot_keys ?? [];
      }
      break;
    }
    case "zone_only":
      out.zone_id = clean(body.zone_id);
      break;
    case "first_come":
    case "flexible_hours":
      out.zone_id = clean(body.zone_id);
      break;
    case "no_seat":
      break;
  }
  return out;
}

function withSeconds(hhmm: string): string {
  return /^\d{2}:\d{2}$/.test(hhmm) ? `${hhmm}:00` : hhmm;
}

/** `09:00:00` → `9:00 AM`. */
export function clockLabel(value: string): string {
  const [hh = "00", mm = "00"] = value.split(":");
  const h = Number(hh);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${suffix}`;
}

/** One line describing what a plan holds, for the table and the drawer. */
export function planDetail(plan: EffectiveSeatPlan): string {
  const parts: string[] = [];
  if (plan.seat_number) parts.push(`Seat ${plan.seat_number}`);
  if (plan.zone_name) parts.push(plan.zone_name);
  if (plan.window_start && plan.window_end) {
    parts.push(`${clockLabel(plan.window_start)} – ${clockLabel(plan.window_end)}`);
  } else if (plan.slot_keys.length > 0) {
    parts.push(plan.slot_keys.join(", "));
  }
  return parts.join(" · ");
}

export function useWalkInCheckIn() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationKey: ["ia", "seating", "walk-in"],
    mutationFn: (studentId) =>
      api.post(`/api/seating/learners/${encodeURIComponent(studentId)}/check-in`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ia", "seating", "occupancy"] });
    },
  });
}
